import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, TextField } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useAuth } from "../../../context/AuthContext";
import MembershipsTable from "./MembershipsTable";
import MembershipFormModal from "./MembershipFormModal";
import MembershipDeleteModal from "./MembershipDeleteModal";

export default function MembershipsPanel() {
  const { authFetch, user } = useAuth();

  const [plans, setPlans] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const canManage = user?.role === "admin";

  const loadData = async () => {
    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch("/api/memberships/plans/", { method: "GET" });
      const data = await response.json().catch(() => []);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudieron cargar las membresías",
        });
        setPlans([]);
        return;
      }

      setPlans(Array.isArray(data) ? data : []);
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al cargar las membresías",
      });
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredPlans = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term) return plans;

    return plans.filter((item) => {
      const name = String(item.name || "").toLowerCase();
      const usageType = String(item.usage_type || "").toLowerCase();
      const durationUnit = String(item.duration_unit || "").toLowerCase();
      const durationValue = String(item.duration_value || "").toLowerCase();
      const price = String(item.price || "").toLowerCase();
      const accesses = Array.isArray(item.available_accesses)
        ? item.available_accesses.join(" ").toLowerCase()
        : "";

      return (
        name.includes(term) ||
        usageType.includes(term) ||
        durationUnit.includes(term) ||
        durationValue.includes(term) ||
        price.includes(term) ||
        accesses.includes(term)
      );
    });
  }, [plans, query]);

  const handleOpenCreate = () => {
    setSelectedPlan(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setSelectedPlan(plan);
    setFormOpen(true);
  };

  const handleCloseForm = (force = false) => {
    if (!force && actionLoadingId) return;
    setFormOpen(false);
    setSelectedPlan(null);
  };

  const handleOpenDelete = (plan) => {
    setSelectedPlan(plan);
    setDeleteOpen(true);
  };

  const handleCloseDelete = (force = false) => {
    if (!force && actionLoadingId) return;
    setDeleteOpen(false);
    setSelectedPlan(null);
  };

  const handleSavePlan = async (payload) => {
    if (!canManage) return { ok: false, error: "No autorizado" };

    const isEditing = Boolean(selectedPlan?.id);
    const planUrl = isEditing
      ? `/api/memberships/plans/${selectedPlan.id}/update/`
      : "/api/memberships/plans/create/";
    const planMethod = isEditing ? "PATCH" : "POST";

    setActionLoadingId(isEditing ? String(selectedPlan.id) : "create-plan");
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(planUrl, {
        method: planMethod,
        body: JSON.stringify({
          name: payload.name,
          price: payload.price,
          duration_value: payload.duration_value,
          duration_unit: payload.duration_unit,
          usage_type: payload.usage_type,
          available_accesses: payload.available_accesses,
          is_active: payload.is_active,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMessage = data?.error || "No se pudo guardar la membresía";
        setBanner({ type: "error", message: errorMessage });
        return { ok: false, error: errorMessage };
      }

      await loadData();

      setBanner({
        type: "success",
        message: data?.message || "Membresía guardada correctamente",
      });

      handleCloseForm(true);
      return { ok: true };
    } catch {
      const errorMessage = "Ocurrió un error al guardar la membresía";
      setBanner({ type: "error", message: errorMessage });
      return { ok: false, error: errorMessage };
    } finally {
      setActionLoadingId("");
    }
  };

  const handleDeletePlan = async () => {
    if (!canManage || !selectedPlan) return;

    setActionLoadingId(String(selectedPlan.id));
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(`/api/memberships/plans/${selectedPlan.id}/delete/`, {
        method: "DELETE",
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo eliminar la membresía",
        });
        return;
      }

      setPlans((prev) => prev.filter((item) => item.id !== selectedPlan.id));
      setBanner({
        type: "success",
        message: data?.message || "Membresía eliminada correctamente",
      });
      handleCloseDelete(true);
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al eliminar la membresía",
      });
    } finally {
      setActionLoadingId("");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.4}
        sx={{ mb: 1.8 }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar membresía"
            size="small"
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ fontSize: 18, color: "#64748b", mr: 0.8 }} />,
            }}
            sx={{
              minWidth: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                bgcolor: "#ffffff",
              },
            }}
          />

          <Button
            onClick={loadData}
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            sx={{
              height: 38,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              bgcolor: "#ffffff",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "#f8fafc",
              },
            }}
          >
            Recargar
          </Button>
        </Stack>

        {canManage && (
          <Button
            onClick={handleOpenCreate}
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{
              height: 38,
              px: 1.6,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            Nueva membresía
          </Button>
        )}
      </Stack>

      {banner.message && (
        <Alert
          severity={banner.type}
          sx={{
            borderRadius: "12px",
            mb: 1.6,
          }}
        >
          {banner.message}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <MembershipsTable
          plans={filteredPlans}
          canManage={canManage}
          actionLoadingId={actionLoadingId}
          onEdit={handleOpenEdit}
          onDelete={handleOpenDelete}
        />
      )}

      <MembershipFormModal
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSavePlan}
        plan={selectedPlan}
        loading={
          actionLoadingId === "create-plan" ||
          (selectedPlan?.id ? actionLoadingId === String(selectedPlan.id) : false)
        }
      />

      <MembershipDeleteModal
        open={deleteOpen}
        onClose={handleCloseDelete}
        onConfirm={handleDeletePlan}
        plan={selectedPlan}
        loading={selectedPlan?.id ? actionLoadingId === String(selectedPlan.id) : false}
      />
    </Box>
  );
}